import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';

/** Material UI Packages. */
import { Dialog, Box } from '@mui/material';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { addTradePartner } from 'shared/store/slices/tradePartnerSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import CircularLoading from 'shared/ui/CircularLoading';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import DialogHeader from 'shared/ui/DialogHeader';
import { PaperComponent } from 'shared/ui/DraggableDialog';
import validateFields from 'shared/lib/validateFields';
import { createSocketData, readSocketData } from 'shared/lib/fetchSocket';
import { getSocket } from 'socket';
import { handleDialogCloseWithDetection, detectChanges } from 'shared/lib/changeDetection';

import PartnerInfo from './PartnerInfo';
import { initTradePartnerData, initTradePartnerValidator } from 'data/tradePartnerConfig';

/** How long a save may wait for the server's answer before the dialog is released. */
const ACK_TIMEOUT_MS = 10000;

const NO_ANSWER_MESSAGE =
    'The server did not answer. Please check whether the change was saved before trying again.';

/**
 * Guards the callbacks handed to the socket helper so the caller always hears back exactly once.
 *
 * The plain ack callback the helper registers is silently dropped by socket.io when the
 * connection closes, and it never fires if the server hangs. A dialog that locks itself until
 * the ack arrives would then stay locked until a page refresh. This adds the two missing exits
 * (an ack timeout and a `disconnect` listener) and folds every outcome into one settle step,
 * so the lock is released in every case. Pass the returned onSuccess/onFailure to the helper;
 * `onFinally` runs here, once, after whichever outcome wins.
 */
const guardSocketAck = (dispatch, { onSuccess, onFailure, onFinally, timeoutMs = ACK_TIMEOUT_MS }) => {
    const socket = getSocket();
    let settled = false;
    let timer = null;

    const onDisconnect = () => giveUp();

    const settle = (finish) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        socket?.off?.('disconnect', onDisconnect);
        finish();
        onFinally?.();
    };

    function giveUp() {
        settle(() => {
            onFailure?.();
            dispatch(setSnackbar({ open: true, message: NO_ANSWER_MESSAGE, severity: 'error', autoHideDuration: 10000 }));
        });
    }

    socket?.on?.('disconnect', onDisconnect);
    timer = setTimeout(giveUp, timeoutMs);

    return {
        onSuccess: (response) => settle(() => onSuccess?.(response)),
        onFailure: (response) => settle(() => onFailure?.(response)),
    };
};

export default function NewTradePartnerDialog(props) {
    /** Get props from TradePartner.js. */
    const { newOpen, setNewOpen } = props;

    /** Dispatch a function to update an edited trade partner. */
    const dispatch = useDispatch();
    const socketConnected = useSelector((state) => state.socketStatus.socketStatus);

    /** Partner type dropdown options from server (read `il_trade_partner` / `partner_type_options`). */
    const [partnerTypeOptions, setPartnerTypeOptions] = useState([]);

    /** State for trade partner inputs. */
    const [tradePartnerForm, setTradePartnerForm] = useState(initTradePartnerData);
    /**
     * The form as the server set it up (partner_type defaulted from the option list).
     * Dirty checks compare against this, not initTradePartnerData, so a server-side
     * default that differs from the client constant does not count as user input.
     */
    const [baselineForm, setBaselineForm] = useState(initTradePartnerData);
    /** State for validate required input data. */
    const [tradePartnerValidator, setTradePartnerValidator] = useState(initTradePartnerValidator);
    /** State for indicating whether a new trade partner is being created. */
    const [circularLoad, setCircularLoad] = useState(false);

    const resetState = () => {
        setTradePartnerForm(initTradePartnerData);
        setBaselineForm(initTradePartnerData);
        setTradePartnerValidator(initTradePartnerValidator);
        setNewOpen(false);
    };

    useEffect(() => {
        if (!newOpen || !socketConnected) {
            return;
        }
        readSocketData(
            dispatch,
            '',
            'cell',
            'il_trade_partner',
            'partnerTypeOptions',
            '',
            (receivedData) => {
                if (!Array.isArray(receivedData) || receivedData.length === 0) {
                    return;
                }
                setPartnerTypeOptions(receivedData);
                const resolveType = (prev) =>
                    receivedData.some((o) => o.content === prev.partner_type)
                        ? prev.partner_type
                        : receivedData[0].content;
                setTradePartnerForm((prev) => ({ ...prev, partner_type: resolveType(prev) }));
                // Only partner_type moves into the baseline: anything the user typed before
                // the options arrived must still register as a change.
                setBaselineForm((prev) => ({ ...prev, partner_type: resolveType(prev) }));
            },
        );
    }, [newOpen, socketConnected, dispatch]);

    /** Whether the form differs from its server-set baseline (same rule as the close confirmation). */
    const isDirty = useMemo(() => detectChanges(baselineForm, tradePartnerForm), [baselineForm, tradePartnerForm]);

    /** Handler for sending POST request to create a new trade partner. */
    const onClickAddBtn = () => {
        // Ignore repeated clicks while a create request is in flight so the same payload
        // is not emitted twice.
        if (circularLoad) return;

        // Validate create condition before making WebSocket call.
        const validationRules = {
            partner_type: tradePartnerForm.partner_type,
            fullname: tradePartnerForm.fullname,
            name: tradePartnerForm.name,
            address_one: tradePartnerForm.address_one,
        };

        const isValid = validateFields(validationRules, setTradePartnerValidator);

        if (!isValid) {
            dispatch(setSnackbar({ open: true, message: 'Please complete all required fields.', severity: 'warning' }));
            return;
        }
        // Convert all string values to uppercase, except the free-form note fields.
        const asIsFields = ['partner_type', 'remark'];
        const uppercaseData = Object.fromEntries(
            Object.entries(tradePartnerForm).map(([key, value]) => [
                key,
                asIsFields.includes(key) ? value : typeof value === 'string' ? value.toUpperCase() : value,
            ]),
        );

        // Show loading and lock Save until the server answers. guardSocketAck releases the
        // lock on success, failure, ack timeout and disconnect, so it cannot stay stuck.
        setCircularLoad(true);
        const ack = guardSocketAck(dispatch, {
            onSuccess: (receivedData) => {
                dispatch(addTradePartner(receivedData.data));
                resetState();
            },
            onFinally: () => setCircularLoad(false),
        });

        // Call create request, add new trade partner in data grid and initialize validator state.
        createSocketData(dispatch, '', 'row', 'il_trade_partner', '', uppercaseData, ack.onSuccess, ack.onFailure);
    };

    /** Handler for closing the dialog with change detection. */
    const handleClose = () => {
        handleDialogCloseWithDetection(baselineForm, tradePartnerForm, null, resetState);
    };

    return (
        <>
            <CircularLoading loading={circularLoad} />
            <Dialog
                onClose={handleClose}
                open={newOpen}
                maxWidth="md"
                fullWidth={true}
                PaperProps={{ sx: { borderRadius: '18px' } }}
                PaperComponent={PaperComponent}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <DialogHeader
                        cursor
                        title="NEW"
                        onClose={handleClose}
                        onSave={onClickAddBtn}
                        saveDisabled={!isDirty || circularLoad}
                    />
                    <PartnerInfo
                        tradePartnerForm={tradePartnerForm}
                        setTradePartnerForm={setTradePartnerForm}
                        tradePartnerValidator={tradePartnerValidator}
                        partnerTypeOptions={partnerTypeOptions}
                        purpose="new"
                        spacing={2}
                    />
                </Box>
            </Dialog>
            <CustomSnackbar />
        </>
    );
}
